import Accordion from '@mui/material/Accordion'
import AccordionDetails from '@mui/material/AccordionDetails'
import AccordionSummary from '@mui/material/AccordionSummary'
import Box from '@mui/material/Box'
import Divider from '@mui/material/Divider'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import Grid from '@mui/material/Grid2'
import { useLanguageData } from 'hooks/data'
import { useTranslation } from 'react-i18next'
import { ILanguageSkill } from 'types/cvsInterfaces'
import { IItemVisibility, ILayout } from 'types/layoutInterfaces'
import { languageLevels } from 'utils/utils'
import SkillChip from '../SkillChip'
import VisibilityCheckbox from '../VisibilityCheckbox'
import colors from 'constants/colors'

interface ILanguageSkillsProps {
  languages: ILanguageSkill[]
  layout: ILayout
  hideSection: (value: boolean, type: string) => void
  hideItem: (value: boolean, id: number, type: string) => void
  expanded: boolean
  setStatus: (value: boolean) => void
}

/**
 * Renders the LanguageSkills component.
 *
 * @param {Object} languages - The list of languages.
 * @param {Object} layout - The layout configuration.
 * @param {boolean} hideSection - Indicates if the section should be hidden.
 * @param {boolean} hideItem - Indicates if the item should be hidden.
 * @param {boolean} expanded - Indicates if the accordion is expanded.
 * @param {function} setStatus - Sets the status of the accordion.
 * @return {React.ReactElement} The rendered LanguageSkills component.
 * @notExported
 */
const LanguageSkills: React.FC<ILanguageSkillsProps> = ({
  languages,
  layout,
  hideSection,
  hideItem,
  expanded,
  setStatus,
}) => {
  const { t } = useTranslation()
  const { languages: options } = useLanguageData()

  let hidden = false
  if (layout && layout.ItemVisibilities && layout.ItemVisibilities.length > 0) {
    const visibilities = layout.ItemVisibilities.filter(
      (visibility: IItemVisibility) => visibility.type === 'languages'
    )
    if (visibilities.length > 0) {
      const visibilityMap = new Map(visibilities.map(item => [item.itemId, item.hidden]))
      hidden = languages.every(value => visibilityMap.get(value.id) === true)
    }
  }

  const native: ILanguageSkill[] = []
  const excellent: ILanguageSkill[] = []
  const good: ILanguageSkill[] = []
  const manageable: ILanguageSkill[] = []
  const basics: ILanguageSkill[] = []

  for (const language of languages) {
    if (language.level === 5) {
      native.push(language)
    }
    if (language.level === 4) {
      excellent.push(language)
    }
    if (language.level === 3) {
      good.push(language)
    }
    if (language.level === 2) {
      manageable.push(language)
    }
    if (language.level === 1) {
      basics.push(language)
    }
  }

  const getLanguageSection = (count: number, array: ILanguageSkill[]) => {
    return (
      <>
        <Box display="flex" flexDirection="row">
          <Box fontWeight={'bold'}>{t(languageLevels()[count - 1].label)}</Box>
        </Box>
        <Box display="block" width="100%" my={2}>
          {array.map(language => {
            let hiddenItem: boolean | IItemVisibility = false
            if (layout && layout.ItemVisibilities && layout.ItemVisibilities.length > 0) {
              const visibilities = layout.ItemVisibilities.filter(
                (visibility: IItemVisibility) => visibility.type === 'languages'
              )
              if (visibilities.length > 0) {
                hiddenItem =
                  visibilities.find(
                    (value: IItemVisibility) => value.itemId === language.id && value.hidden === true
                  ) ?? false
              }
            }
            return (
              <SkillChip
                hidden={!!hiddenItem}
                key={language.id}
                name={options ? options[language.languageCode] : language.languageCode}
                itemId={language.id}
                type="languages"
                onClick={hideItem}
              />
            )
          })}
        </Box>
        {count > 1 && <Divider />}
      </>
    )
  }

  return (
    <Accordion
      expanded={expanded}
      onChange={(_e, expanded) => setStatus(expanded)}
      elevation={0}
      sx={{ borderRadius: '5px', border: `1px solid ${colors.borderColor}` }}
      slotProps={{
        transition: { unmountOnExit: true },
      }}
    >
      <Box display="flex" alignItems="center">
        <VisibilityCheckbox checked={hidden} onChange={(checked: boolean) => hideSection(checked, 'languages')} />
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          sx={{
            flex: 1,
            fontSize: '20px',
            color: theme => theme.palette.primary.main,
            fontWeight: 'bold',
          }}
        >
          <Grid>{t('print.layout.languages')}</Grid>
        </AccordionSummary>
      </Box>
      <AccordionDetails>
        <Grid size={12}>
          {getLanguageSection(5, native)}
          {getLanguageSection(4, excellent)}
          {getLanguageSection(3, good)}
          {getLanguageSection(2, manageable)}
          {getLanguageSection(1, basics)}
        </Grid>
      </AccordionDetails>
    </Accordion>
  )
}

export default LanguageSkills
